/**
 * Mobile Tap-to-Zoom Lightbox
 * Activates only on screens < 768px
 * Adds tap-to-zoom for qualifying images
 */

(function () {
  const MOBILE_QUERY = '(max-width: 767px)';
  const MIN_IMAGE_WIDTH = 100;
  const MAX_ZOOM = 4;
  const MIN_ZOOM = 1;
  const SWIPE_DISMISS_THRESHOLD = 100;
  const ANIMATION_DURATION = 300;

  let overlay = null;
  let currentImage = null;
  let triggerElement = null;
  let savedOverflow = '';

  // Pinch state
  let initialPinchDistance = 0;
  let currentScale = 1;
  let pinchCenterX = 0;
  let pinchCenterY = 0;

  // Swipe state
  let swipeStartY = 0;
  let swipeCurrentY = 0;
  let isSwiping = false;

  // RAF handle
  let rafId = null;

  const mql = window.matchMedia(MOBILE_QUERY);

  /**
   * Get the best available source URL for an image.
   * Handles WP Rocket lazy loading (data-src, data-lazy-src) and standard src.
   */
  function getImageSrc(img) {
    // WP Rocket / lazy-load plugins store the real URL in data attributes
    var lazySrc = img.getAttribute('data-lazy-src');
    if (lazySrc) return lazySrc;

    var dataSrc = img.getAttribute('data-src');
    if (dataSrc) return dataSrc;

    return img.getAttribute('src') || '';
  }

  /**
   * Check if an image qualifies for the lightbox
   */
  function isQualifyingImage(img) {
    if (img.getAttribute('data-lightbox') === 'false') return false;
    if (img.getAttribute('data-lightbox') !== 'true') return false;
    var src = getImageSrc(img).toLowerCase();
    if (src.endsWith('.svg')) return false;
    if (img.naturalWidth > 0 && img.naturalWidth < MIN_IMAGE_WIDTH) return false;
    if (img.closest('.swiper')) return false;
    return true;
  }

  /**
   * Discover and bind qualifying images
   */
  function bindImages() {
    var images = document.querySelectorAll('img[data-lightbox="true"]');
    images.forEach(function (img) {
      if (img._lightboxBound) return;
      img._lightboxBound = true;

      function handleClick(e) {
        if (!isQualifyingImage(img)) return;

        // If the image is inside a link, prevent navigation
        var parentLink = img.closest('a');
        if (parentLink) {
          e.preventDefault();
          e.stopPropagation();
        }

        openLightbox(img);
      }

      img.addEventListener('click', handleClick);
      img._lightboxCleanup = function () {
        img.removeEventListener('click', handleClick);
        img._lightboxBound = false;
        delete img._lightboxCleanup;
      };
    });
  }

  /**
   * Unbind all lightbox click handlers
   */
  function unbindImages() {
    var images = document.querySelectorAll('img[data-lightbox="true"]');
    images.forEach(function (img) {
      if (img._lightboxCleanup) img._lightboxCleanup();
    });
  }

  /**
   * Create the close button SVG
   */
  function createCloseButton() {
    var btn = document.createElement('button');
    btn.className = 'dg-lightbox__close';
    btn.setAttribute('type', 'button');
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.12 5.71a1 1 0 0 0-1.42 1.42L10.59 12l-4.89 4.88a1 1 0 1 0 1.42 1.42L12 13.41l4.88 4.89a1 1 0 0 0 1.42-1.42L13.41 12l4.89-4.88a1 1 0 0 0 0-1.41z"/>' +
      '</svg>';
    return btn;
  }

  /**
   * Lock body scroll
   */
  function lockScroll() {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Unlock body scroll
   */
  function unlockScroll() {
    document.body.style.overflow = savedOverflow;
  }

  /**
   * Open the lightbox for a given image
   */
  function openLightbox(img) {
    if (overlay) return;

    triggerElement = img;

    // Create overlay
    overlay = document.createElement('div');
    overlay.className = 'dg-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image viewer');

    // Clone image at full resolution using the best available source
    var fullSrc = getImageSrc(img);

    currentImage = document.createElement('img');
    currentImage.className = 'dg-lightbox__image';
    currentImage.src = fullSrc;
    currentImage.alt = img.alt || '';

    // Only copy srcset/sizes from the img element (not from <source> in <picture>)
    if (img.srcset) {
      currentImage.srcset = img.srcset;
    }
    if (img.sizes) {
      currentImage.sizes = img.sizes;
    }

    var closeBtn = createCloseButton();

    overlay.appendChild(closeBtn);
    overlay.appendChild(currentImage);
    document.body.appendChild(overlay);

    lockScroll();

    // Force reflow then activate
    overlay.offsetHeight; // eslint-disable-line no-unused-expressions
    overlay.classList.add('is-active');

    // Event listeners
    closeBtn.addEventListener('click', closeLightbox);
    overlay.addEventListener('click', handleOverlayClick);
    document.addEventListener('keydown', handleKeydown);

    // Touch gesture listeners (passive where possible)
    currentImage.addEventListener('touchstart', handleTouchStart, { passive: false });
    currentImage.addEventListener('touchmove', handleTouchMove, { passive: false });
    currentImage.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Focus the close button
    closeBtn.focus();

    // Reset gesture state
    currentScale = 1;
    isSwiping = false;
    initialPinchDistance = 0;
  }

  /**
   * Close the lightbox
   */
  function closeLightbox() {
    if (!overlay) return;

    overlay.classList.remove('is-active');
    overlay.classList.add('is-closing');

    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    // Clean up event listeners
    document.removeEventListener('keydown', handleKeydown);

    var overlayRef = overlay;
    var returnFocus = triggerElement;

    setTimeout(function () {
      if (overlayRef && overlayRef.parentNode) {
        overlayRef.parentNode.removeChild(overlayRef);
      }
      unlockScroll();
      if (returnFocus) {
        returnFocus.focus({ preventScroll: true });
      }
    }, ANIMATION_DURATION);

    overlay = null;
    currentImage = null;
    triggerElement = null;
  }

  /**
   * Handle click on overlay background (not on image or close button)
   */
  function handleOverlayClick(e) {
    if (e.target === overlay) {
      closeLightbox();
    }
  }

  /**
   * Handle keyboard events
   */
  function handleKeydown(e) {
    if (e.key === 'Escape') {
      closeLightbox();
      return;
    }

    // Focus trap: only the close button is focusable
    if (e.key === 'Tab' && overlay) {
      var closeBtn = overlay.querySelector('.dg-lightbox__close');
      if (closeBtn) {
        e.preventDefault();
        closeBtn.focus();
      }
    }
  }

  /**
   * Get distance between two touch points
   */
  function getTouchDistance(t1, t2) {
    var dx = t1.clientX - t2.clientX;
    var dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Handle touch start
   */
  function handleTouchStart(e) {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      initialPinchDistance = getTouchDistance(e.touches[0], e.touches[1]);
      pinchCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      pinchCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      isSwiping = false;
    } else if (e.touches.length === 1 && currentScale <= 1) {
      // Potential swipe start (only when not zoomed)
      swipeStartY = e.touches[0].clientY;
      swipeCurrentY = swipeStartY;
      isSwiping = true;
    }
  }

  /**
   * Handle touch move
   */
  function handleTouchMove(e) {
    if (e.touches.length === 2 && initialPinchDistance > 0) {
      // Pinch zoom
      e.preventDefault();
      var newDist = getTouchDistance(e.touches[0], e.touches[1]);
      var scaleChange = newDist / initialPinchDistance;
      var newScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentScale * scaleChange));

      initialPinchDistance = newDist;
      currentScale = newScale;

      // Update pinch center
      pinchCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      pinchCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

      updateTransform();
    } else if (e.touches.length === 1 && isSwiping) {
      // Swipe to dismiss
      e.preventDefault();
      swipeCurrentY = e.touches[0].clientY;
      var translateY = swipeCurrentY - swipeStartY;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        if (currentImage) {
          currentImage.style.transform = 'translateY(' + translateY + 'px)';
        }
      });
    }
  }

  /**
   * Handle touch end
   */
  function handleTouchEnd(e) {
    if (e.touches.length === 0 && isSwiping) {
      var distance = Math.abs(swipeCurrentY - swipeStartY);
      if (distance > SWIPE_DISMISS_THRESHOLD) {
        closeLightbox();
      } else {
        // Snap back
        if (currentImage) {
          currentImage.style.transform = '';
        }
      }
      isSwiping = false;
    }

    // Reset pinch when all fingers lifted
    if (e.touches.length < 2) {
      initialPinchDistance = 0;
    }
  }

  /**
   * Update the image transform for pinch zoom
   */
  function updateTransform() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function () {
      if (currentImage) {
        var rect = currentImage.getBoundingClientRect();
        var originX = ((pinchCenterX - rect.left) / rect.width) * 100;
        var originY = ((pinchCenterY - rect.top) / rect.height) * 100;
        currentImage.style.transformOrigin = originX + '% ' + originY + '%';
        currentImage.style.transform = 'scale(' + currentScale + ')';
      }
    });
  }

  /**
   * Handle media query change
   */
  function handleMediaChange(e) {
    if (e.matches) {
      // Entered mobile
      bindImages();
    } else {
      // Left mobile
      if (overlay) closeLightbox();
      unbindImages();
    }
  }

  /**
   * Initialize on DOMContentLoaded
   */
  document.addEventListener('DOMContentLoaded', function () {
    if (mql.matches) {
      bindImages();
    }

    // Listen for viewport changes
    if (mql.addEventListener) {
      mql.addEventListener('change', handleMediaChange);
    } else if (mql.addListener) {
      mql.addListener(handleMediaChange);
    }
  });
})();
